import { ReactNode, useState, useMemo, useRef, useEffect } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  pointerWithin,
  CollisionDetection,
} from "@dnd-kit/core";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanCard, KanbanItem } from "./KanbanCard";

const REFUSAL_FEEDBACK_MS = 600;

interface KanbanStatus<TStatus extends string> {
  id: TStatus;
  title: string;
  /** Rendered in the column header before the count. */
  badge?: ReactNode;
}

/** Per-column metadata for pagination display. */
interface ColumnMeta {
  total: number;
  isLoadingMore: boolean;
}

interface KanbanBoardProps<T extends KanbanItem, TStatus extends string> {
  items: T[];
  statuses: readonly KanbanStatus<TStatus>[];
  onItemClick: (id: string) => void;
  /** Resolves true when the move was accepted, false when it was refused. */
  onStatusChange: (itemId: string, newStatus: TStatus) => Promise<boolean>;
  renderItem: (item: T) => ReactNode;
  className?: string;
  /** Optional per-column metadata (total count, loading state) for pagination. */
  columnMeta?: Map<TStatus, ColumnMeta>;
  /** Callback to load the next page of items for a column. */
  onLoadMore?: (status: TStatus) => void;
  /**
   * Notifies when a drag starts (true) and ends (false), so callers can defer
   * external item-list changes (e.g. live sync) until the drag settles.
   */
  onDragActiveChange?: (active: boolean) => void;
}

interface OptimisticUpdate<TStatus extends string> {
  itemId: string;
  newStatus: TStatus;
  originalStatus: TStatus;
  requestId: number;
}

/** A move the caller refused, played back as feedback on the card and target column. */
interface RefusedMove<TStatus extends string> {
  itemId: string;
  targetStatus: TStatus;
  settleFrom: "left" | "right";
}

/**
 * A generic drag-and-drop Kanban board with optional per-column pagination.
 * @param items - All items to display across columns
 * @param statuses - Column definitions (id + title)
 * @param onItemClick - Callback when an item card is clicked
 * @param onStatusChange - Callback when an item is dragged to a new status column; resolving false refuses the move, settling the card back and flashing the target column
 * @param renderItem - Render function for item card content
 * @param className - Optional additional CSS classes
 * @param columnMeta - Optional per-column metadata for pagination (total, isLoadingMore)
 * @param onLoadMore - Optional callback to load more items for a column
 * @param onDragActiveChange - Optional notifier for drag start (true) / settle (false)
 * @returns A Kanban board with drag-and-drop and optional pagination
 */
export function KanbanBoard<T extends KanbanItem, TStatus extends string>({
  items,
  statuses,
  onItemClick,
  onStatusChange,
  renderItem,
  className,
  columnMeta,
  onLoadMore,
  onDragActiveChange,
}: KanbanBoardProps<T, TStatus>) {
  const [activeItem, setActiveItem] = useState<T | null>(null);
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Map<string, OptimisticUpdate<TStatus>>
  >(new Map());

  const [refusedMove, setRefusedMove] = useState<RefusedMove<TStatus> | null>(
    null,
  );

  const requestIdsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!refusedMove) {
      return;
    }
    const timeout = setTimeout(() => setRefusedMove(null), REFUSAL_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [refusedMove]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Collision detection strategy to allow dropping into columns even when hovering over other cards
  const collisionDetectionStrategy: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    const columnCollisions = pointerCollisions.filter((collision) => {
      const data = args.droppableContainers.find(
        (container) => container.id === collision.id,
      )?.data.current;
      return data?.type === "column";
    });

    if (columnCollisions.length > 0) {
      return columnCollisions;
    }

    return closestCenter(args);
  };

  const itemsWithOptimisticUpdates = useMemo(() => {
    return items.map((item) => {
      const update = optimisticUpdates.get(item.id);
      if (update) {
        return { ...item, status: update.newStatus };
      }
      return item;
    });
  }, [items, optimisticUpdates]);

  const itemsByStatus = useMemo(() => {
    const grouped = {} as Record<TStatus, T[]>;
    statuses.forEach((status) => {
      grouped[status.id] = [];
    });

    itemsWithOptimisticUpdates.forEach((item) => {
      const status = item.status as TStatus;
      if (status in grouped) {
        grouped[status].push(item);
      }
    });

    return grouped;
  }, [itemsWithOptimisticUpdates, statuses]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const item = itemsWithOptimisticUpdates.find((i) => i.id === active.id);
    if (item) {
      setActiveItem(item);
      onDragActiveChange?.(true);
    }
  };

  const handleDragCancel = () => {
    setActiveItem(null);
    onDragActiveChange?.(false);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItem(null);
    onDragActiveChange?.(false);

    if (!over) {
      return;
    }

    const itemId = active.id as string;
    // Use optimistic state to get the item's current visible status
    // This ensures we compare against what the user sees, not backend state
    const itemWithOptimisticUpdate = itemsWithOptimisticUpdates.find(
      (i) => i.id === itemId,
    );
    // Also get the backend state for storing the true original status
    const backendItem = items.find((i) => i.id === itemId);

    if (!itemWithOptimisticUpdate || !backendItem) {
      return;
    }

    const overData = over.data.current;
    if (!overData || overData.type !== "column") {
      return;
    }

    const newStatus = over.id as TStatus;
    const currentVisibleStatus = itemWithOptimisticUpdate.status as TStatus;

    // Check against what the user currently sees (including optimistic updates)
    if (currentVisibleStatus === newStatus) {
      return;
    }

    // Increment request ID for this item (e.g., 1 -> 2 -> 3 for rapid moves)
    // This ensures each API call has a unique sequence number
    const currentRequestId = (requestIdsRef.current.get(itemId) ?? 0) + 1;
    requestIdsRef.current.set(itemId, currentRequestId);

    // Apply optimistic update immediately for smooth UX
    // Store the requestId so we can validate responses later
    // Use backend's original status for proper error handling/reversion
    setOptimisticUpdates((prev) => {
      const next = new Map(prev);
      next.set(itemId, {
        itemId,
        newStatus,
        originalStatus: backendItem.status as TStatus,
        requestId: currentRequestId,
      });
      return next;
    });

    try {
      const accepted = await onStatusChange(itemId, newStatus);
      const isLatestRequest =
        requestIdsRef.current.get(itemId) === currentRequestId;
      if (!accepted && isLatestRequest) {
        setRefusedMove({
          itemId,
          targetStatus: newStatus,
          settleFrom: getSettleSide(
            statuses.map((status) => status.id),
            backendItem.status,
            newStatus,
          ),
        });
      }

      // Only clear optimistic update if this response is from the latest request
      // If user moved the card again, a newer requestId exists, so ignore this response
      setOptimisticUpdates((prev) => {
        const next = new Map(prev);
        const currentUpdate = next.get(itemId);

        // Check: Is this response for the most recent move?
        if (currentUpdate && currentUpdate.requestId === currentRequestId) {
          // Yes - clear the optimistic update, backend is now synced
          next.delete(itemId);
        }
        // No - ignore this response, a newer move has occurred
        return next;
      });
    } catch (_error) {
      // On error, only revert if this was the latest request
      setOptimisticUpdates((prev) => {
        const next = new Map(prev);
        const currentUpdate = next.get(itemId);

        // Check: Is this error for the most recent move?
        if (currentUpdate && currentUpdate.requestId === currentRequestId) {
          // Yes - clear optimistic update to revert to backend state
          next.delete(itemId);
        }
        // No - ignore this error, a newer move has occurred
        return next;
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className={className}>
        {statuses.map((status) => {
          const meta = columnMeta?.get(status.id);
          return (
            <KanbanColumn
              key={status.id}
              id={status.id}
              title={status.title}
              items={itemsByStatus[status.id]}
              onItemClick={onItemClick}
              renderItem={renderItem}
              total={meta?.total}
              isLoadingMore={meta?.isLoadingMore}
              onLoadMore={onLoadMore ? () => onLoadMore(status.id) : undefined}
              badge={status.badge}
              isRefusingDrop={refusedMove?.targetStatus === status.id}
              settlingItemId={refusedMove?.itemId}
              settleFrom={refusedMove?.settleFrom}
            />
          );
        })}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <div className="scale-[1.02] rotate-[0.5deg] rounded-lg shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12),0_2px_8px_-2px_rgba(0,0,0,0.08)]">
            <KanbanCard
              item={activeItem}
              onClick={() => {}}
              renderContent={renderItem}
              className="shadow-none border-primary/20"
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * Picks the side a refused card settles back from: the side of the column it was dropped on.
 * @param statusIds - The column ids in board order
 * @param sourceStatus - The column the card returns to
 * @param targetStatus - The column that refused the card
 * @returns "right" when the target is right of the source, otherwise "left"
 */
function getSettleSide(
  statusIds: string[],
  sourceStatus: string,
  targetStatus: string,
): "left" | "right" {
  if (statusIds.indexOf(targetStatus) > statusIds.indexOf(sourceStatus)) {
    return "right";
  }
  return "left";
}
